import { Add, Delete, Edit } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  createBond,
  deleteBond,
  getBondPositions,
  getBonds,
  getBrokers,
  updateBond,
} from "../../../services/portfolioService";
import { useCurrency } from "../../Auth/AuthContext";

const BondsPage = () => {
  const [bonds, setBonds] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBond, setCurrentBond] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  // Use CurrencyContext for conversion (same as PortfolioSummary)
  const { selectedCurrency, convert, formatMoney } = useCurrency();

  const [formData, setFormData] = useState({
    broker_id: "",
    bond_name: "",
    issuer: "",
    isin: "",
    maturity_date: "",
    coupon_rate: "",
    transaction_type: "BUY",
    face_value: "",
    quantity: "",
    purchase_price: "",
    total_cost: "",
    transaction_date: new Date().toISOString().split("T")[0],
    currency: "USD",
    fees: 0,
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bondsRes, positionsRes, brokersRes] = await Promise.all([
        getBonds(),
        getBondPositions(),
        getBrokers(),
      ]);
      setBonds(bondsRes);
      setPositions(positionsRes);
      setBrokers(brokersRes);
    } catch (err) {
      console.error("Error fetching bonds:", err);
      setError("Failed to load bonds");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (bond = null) => {
    if (bond) {
      setEditMode(true);
      setCurrentBond(bond);
      setFormData({
        broker_id: bond.broker_id || "",
        bond_name: bond.bond_name,
        issuer: bond.issuer || "",
        isin: bond.isin || "",
        maturity_date: bond.maturity_date || "",
        coupon_rate: bond.coupon_rate || "",
        transaction_type: bond.transaction_type,
        face_value: bond.face_value,
        quantity: bond.quantity,
        purchase_price: bond.purchase_price || bond.face_value,
        total_cost: bond.total_cost,
        transaction_date: bond.transaction_date,
        currency: bond.currency || "USD",
        fees: bond.fees || 0,
        notes: bond.notes || "",
      });
    } else {
      setEditMode(false);
      setCurrentBond(null);
      setFormData({
        broker_id: "",
        bond_name: "",
        issuer: "",
        isin: "",
        maturity_date: "",
        coupon_rate: "",
        transaction_type: "BUY",
        face_value: "",
        quantity: "",
        purchase_price: "",
        total_cost: "",
        transaction_date: new Date().toISOString().split("T")[0],
        currency: "USD",
        fees: 0,
        notes: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setCurrentBond(null);
  };

  const handleSubmit = async () => {
    try {
      // Auto-calculate total_cost if not provided
      const finalData = {
        ...formData,
        total_cost:
          formData.total_cost ||
          (formData.purchase_price || formData.face_value) * formData.quantity,
      };

      if (editMode) {
        await updateBond(currentBond.id, finalData);
      } else {
        await createBond(finalData);
      }

      handleCloseDialog();
      fetchData();
    } catch (err) {
      console.error("Error saving bond:", err);
      setError(err.response?.data?.message || "Failed to save bond");
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this bond transaction?")
    )
      return;

    try {
      await deleteBond(id);
      fetchData();
    } catch (err) {
      console.error("Error deleting bond:", err);
      setError("Failed to delete bond");
    }
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={600}>
          Bonds
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Bond Transaction
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Current Holdings" />
        <Tab label="All Transactions" />
      </Tabs>

      {/* Current Holdings Tab */}
      {tabValue === 0 && (
        <Paper>
          <Box
            p={2}
            borderBottom={1}
            borderColor="divider"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={600}>
              Current Holdings
            </Typography>
            <Chip
              label={`Displaying in ${selectedCurrency}`}
              size="small"
              variant="outlined"
              sx={{ color: "text.secondary", borderColor: "divider" }}
            />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bond Name</TableCell>
                  <TableCell>Broker</TableCell>
                  <TableCell>Issuer</TableCell>
                  <TableCell>Maturity</TableCell>
                  <TableCell align="right">Coupon %</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Avg Price</TableCell>
                  <TableCell align="right">Current Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        No bond holdings
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  positions.map((pos, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {pos.bond_name}
                        </Typography>
                        {pos.isin && (
                          <Typography variant="caption" color="text.secondary">
                            ISIN: {pos.isin}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{pos.broker_name || "External"}</TableCell>
                      <TableCell>{pos.issuer || "—"}</TableCell>
                      <TableCell>
                        {pos.maturity_date
                          ? new Date(pos.maturity_date).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {pos.coupon_rate ? `${pos.coupon_rate}%` : "—"}
                      </TableCell>
                      <TableCell align="right">{pos.net_quantity}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {selectedCurrency === "DEFAULT"
                            ? formatMoney(
                                pos.avg_purchase_price,
                                pos.currency,
                                pos.currency,
                              )
                            : formatMoney(
                                convert(
                                  pos.avg_purchase_price,
                                  pos.currency,
                                  selectedCurrency,
                                ),
                                selectedCurrency,
                                selectedCurrency,
                              )}
                        </Typography>
                        {selectedCurrency !== "DEFAULT" &&
                          pos.currency !== selectedCurrency && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              (
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: pos.currency,
                                maximumFractionDigits: 2,
                              }).format(pos.avg_purchase_price)}
                              )
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {selectedCurrency === "DEFAULT"
                            ? formatMoney(
                                pos.current_value,
                                pos.currency,
                                pos.currency,
                              )
                            : formatMoney(
                                convert(
                                  pos.current_value,
                                  pos.currency,
                                  selectedCurrency,
                                ),
                                selectedCurrency,
                                selectedCurrency,
                              )}
                        </Typography>
                        {selectedCurrency !== "DEFAULT" &&
                          pos.currency !== selectedCurrency && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              (
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: pos.currency,
                                maximumFractionDigits: 2,
                              }).format(pos.current_value)}
                              )
                            </Typography>
                          )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* All Transactions Tab */}
      {tabValue === 1 && (
        <Paper>
          <Box
            p={2}
            borderBottom={1}
            borderColor="divider"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={600}>
              Transaction History
            </Typography>
            <Chip
              label={`Displaying in ${selectedCurrency}`}
              size="small"
              variant="outlined"
              sx={{ color: "text.secondary", borderColor: "divider" }}
            />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Bond Name</TableCell>
                  <TableCell>Broker</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bonds.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        No bond transactions
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  bonds.map((bond) => (
                    <TableRow key={bond.id}>
                      <TableCell>
                        {new Date(bond.transaction_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={bond.transaction_type}
                          size="small"
                          color={
                            bond.transaction_type === "BUY"
                              ? "success"
                              : bond.transaction_type === "SELL"
                                ? "error"
                                : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {bond.bond_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{bond.broker_name || "External"}</TableCell>
                      <TableCell align="right">{bond.quantity}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {selectedCurrency === "DEFAULT"
                            ? formatMoney(
                                bond.purchase_price,
                                bond.currency,
                                bond.currency,
                              )
                            : formatMoney(
                                convert(
                                  bond.purchase_price,
                                  bond.currency,
                                  selectedCurrency,
                                ),
                                selectedCurrency,
                                selectedCurrency,
                              )}
                        </Typography>
                        {selectedCurrency !== "DEFAULT" &&
                          bond.currency !== selectedCurrency && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              (
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: bond.currency,
                                maximumFractionDigits: 2,
                              }).format(bond.purchase_price)}
                              )
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {selectedCurrency === "DEFAULT"
                            ? formatMoney(
                                bond.total_cost,
                                bond.currency,
                                bond.currency,
                              )
                            : formatMoney(
                                convert(
                                  bond.total_cost,
                                  bond.currency,
                                  selectedCurrency,
                                ),
                                selectedCurrency,
                                selectedCurrency,
                              )}
                        </Typography>
                        {selectedCurrency !== "DEFAULT" &&
                          bond.currency !== selectedCurrency && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              (
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: bond.currency,
                                maximumFractionDigits: 2,
                              }).format(bond.total_cost)}
                              )
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(bond)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(bond.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "Edit Bond Transaction" : "Add Bond Transaction"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField
              select
              label="Broker"
              value={formData.broker_id}
              onChange={(e) =>
                setFormData({ ...formData, broker_id: e.target.value })
              }
            >
              <MenuItem value="">External</MenuItem>
              {brokers.map((broker) => (
                <MenuItem key={broker.id} value={broker.id}>
                  {broker.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Bond Name *"
              value={formData.bond_name}
              onChange={(e) =>
                setFormData({ ...formData, bond_name: e.target.value })
              }
              required
            />

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="Issuer"
                value={formData.issuer}
                onChange={(e) =>
                  setFormData({ ...formData, issuer: e.target.value })
                }
              />
              <TextField
                label="ISIN"
                value={formData.isin}
                onChange={(e) =>
                  setFormData({ ...formData, isin: e.target.value })
                }
              />
            </Box>

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="Maturity Date"
                type="date"
                value={formData.maturity_date}
                onChange={(e) =>
                  setFormData({ ...formData, maturity_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Coupon Rate (%)"
                type="number"
                value={formData.coupon_rate}
                onChange={(e) =>
                  setFormData({ ...formData, coupon_rate: e.target.value })
                }
              />
            </Box>

            <TextField
              select
              label="Transaction Type *"
              value={formData.transaction_type}
              onChange={(e) =>
                setFormData({ ...formData, transaction_type: e.target.value })
              }
              required
            >
              <MenuItem value="BUY">BUY</MenuItem>
              <MenuItem value="SELL">SELL</MenuItem>
              <MenuItem value="MATURITY">MATURITY</MenuItem>
            </TextField>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 2,
              }}
            >
              <TextField
                label="Face Value *"
                type="number"
                value={formData.face_value}
                onChange={(e) =>
                  setFormData({ ...formData, face_value: e.target.value })
                }
                required
              />
              <TextField
                label="Quantity *"
                type="number"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: e.target.value })
                }
                required
              />
              <TextField
                label="Purchase Price"
                type="number"
                value={formData.purchase_price}
                onChange={(e) =>
                  setFormData({ ...formData, purchase_price: e.target.value })
                }
                helperText="Leave empty to use face value"
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 2,
              }}
            >
              <TextField
                label="Transaction Date *"
                type="date"
                value={formData.transaction_date}
                onChange={(e) =>
                  setFormData({ ...formData, transaction_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                select
                label="Currency"
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="RON">RON</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
              </TextField>
              <TextField
                label="Fees"
                type="number"
                value={formData.fees}
                onChange={(e) =>
                  setFormData({ ...formData, fees: e.target.value })
                }
              />
            </Box>

            <TextField
              label="Notes"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BondsPage;
